// 📁 File: /pages/apps/replybot/ReplyBotInbox.jsx
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import AppShell from '../../../components/AppShell';
import { Loader2, RefreshCw, Send, Plus } from 'lucide-react';
import { Instagram, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_API_BASE;

export default function ReplyBotInbox() {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const loadMessages = async (threadId) => {
    const res = await fetchWithAuth(`${API}/api/messages/${threadId}`);
    const data = await res.json();
    setMessages(data);
  };

  const loadThreads = async () => {
    const res = await fetchWithAuth(`${API}/api/messages/threads`);
    const data = await res.json();
    setThreads(data);

    if (!selectedThread && data.length) {
      setSelectedThread(data[0]);
      loadMessages(data[0].threadId);
    } else if (selectedThread) {
      const matched = data.find(t => t.threadId === selectedThread.threadId);
      if (matched) {
        loadMessages(matched.threadId);
      }
    }
  };

  useEffect(() => {
    loadThreads();
    const interval = setInterval(() => {
      loadThreads();
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedThread?.threadId]);

  useEffect(() => {
    if (selectedThread?.threadId) loadMessages(selectedThread.threadId);
  }, [selectedThread]);

  const generateReply = async () => {
    setLoading(true);
    setTimeout(() => {
      const aiReply = selectedThread?.name.includes('Aisha')
        ? 'Hi Aisha! Your appointment is scheduled for tomorrow at 3PM.'
        : 'Hi! Yes, we’re available Friday. Would you prefer 4PM or 6PM?';
      setReply(aiReply);
      setLoading(false);
    }, 1000);
  };

  const sendReply = async () => {
    const newMsg = {
      from: 'bot',
      text: reply,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages(prev => [...prev, newMsg]);

    try {
      await fetchWithAuth(`${API}/api/whatsapp/send-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: selectedThread.threadId, message: reply }),
      });
      setThreads(prev =>
        prev.map(t =>
          t.threadId === selectedThread.threadId ? { ...t, lastMessage: reply } : t
        )
      );
    } catch (err) {
      alert('Failed to send message');
    }

    setReply('');
  };

  const startNewConversation = async () => {
    if (!newPhone || !newMessage) return alert('Phone and message are required');
    setSending(true);

    const formattedPhone = newPhone.startsWith('+') ? newPhone : `+${newPhone}`;
    const id = Date.now();

    try {
      await fetchWithAuth(`${API}/api/whatsapp/send-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: formattedPhone, message: newMessage }),
      });

      const thread = {
        threadId: formattedPhone,
        name: newName || `User ${id}`,
        platform: 'WhatsApp',
        lastMessage: newMessage,
      };

      setThreads(prev => [thread, ...prev]);
      setSelectedThread(thread);
      setShowNewModal(false);
      setNewName('');
      setNewPhone('');
      setNewMessage('');
    } catch (err) {
      alert('Failed to send message');
    }

    setSending(false);
  };

  return (
    <AppShell>
      <div className="col-span-12 space-y-6">
        <div className="flex justify-between items-center px-4 pt-4">
          <h2 className="text-xl font-semibold">Inbox</h2>
          <Button onClick={() => setShowNewModal(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" /> New Conversation
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 h-[80vh]">
          <div className="border-r p-4 overflow-y-auto space-y-2">
            {threads.map(t => {
              const isActive = t.threadId === selectedThread?.threadId;
              const platformIcon = t.platform === 'WhatsApp'
                ? <MessageCircle className="w-4 h-4 text-green-600 inline mr-1" />
                : <Instagram className="w-4 h-4 text-pink-600 inline mr-1" />;

              return (
                <div
                  key={t.threadId}
                  onClick={() => setSelectedThread(t)}
                  className={`mb-2 cursor-pointer transition-all duration-200 rounded-md ${
                    isActive ? 'bg-primary/10 ring-2 ring-inset ring-primary ring-offset-0' : 'bg-white hover:bg-muted/30'
                  }`}
                >
                  <Card>
                    <CardContent className="p-3 flex gap-2 items-start">
                      {isActive && <div className="w-1 h-full bg-primary rounded-sm" />}
                      <div className="flex-1">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground mb-1">{t.lastMessage}</div>
                        <div className="text-xs flex items-center gap-1">
                          {platformIcon}<span>{t.platform}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          <div className="col-span-2 flex flex-col justify-between">
            <div className="p-4 overflow-y-auto flex-grow">
              <h2 className="text-lg font-bold mb-2">{selectedThread?.name}</h2>
              {messages.map((msg, idx) => (
                <div key={idx} className={`mb-2 text-sm flex ${msg.from === 'bot' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`px-3 py-2 rounded-xl max-w-xs ${msg.from === 'bot' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                    <div>{msg.text}</div>
                    <div className="text-[10px] text-right mt-1 text-muted-foreground">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t flex gap-2 items-center sticky bottom-0 bg-white z-10">
              <Input placeholder="Type your reply or use AI..." value={reply} onChange={(e) => setReply(e.target.value)} />
              <Button variant="outline" onClick={generateReply} disabled={loading}>
                {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
              <Button onClick={sendReply} disabled={!reply}>
                <Send className="w-4 h-4 mr-1" /> Send
              </Button>
            </div>
          </div>
        </div>

        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start New Conversation</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Input placeholder="Phone (+971...)" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
              <Input placeholder="Message" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} />
              <Button className="w-full" onClick={startNewConversation} disabled={sending}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send Message</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
